import { Badge, Button, Tabs, TabsList, TabsTrigger } from "@devhop/ui";
import { useNavigate } from "@tanstack/react-router";
import {
  CalendarIcon,
  DownloadIcon,
  ExternalLinkIcon,
  XIcon,
} from "lucide-react";
import { useMemo, useState } from "react";
import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { Breadcrumb } from "@/shared/ui/Breadcrumb";
import { InfiniteCombobox } from "@/shared/ui/InfiniteCombobox";
import { SimpleSelect } from "@/shared/ui/SimpleSelect";
import {
  type DocumentCurrency,
  formatDocumentMoney,
} from "@/shared/lib/performance-document-currency";
import type { DocumentListItemDTO } from "../../domain/contracts";
import { performanceApi } from "../api/client";
import { useDocumentsQuery } from "../api/queries";
import { PerformanceBrandPrintMenu } from "../ui/PerformanceBrandPrintMenu";

const DOCUMENT_TYPE_LABELS = {
  quotation: "ໃບສະເໜີລາຄາ",
  invoice: "ໃບເກັບເງິນ",
  receipt: "ໃບຮັບເງິນ",
} as const;

const DOCUMENT_STATUS_LABELS = {
  draft: "ຮ່າງ",
  pending_approval: "ລໍຖ້າອະນຸມັດ",
  approved: "ອະນຸມັດແລ້ວ",
  issued: "ອອກແລ້ວ",
  partial: "ຊຳລະບາງສ່ວນ",
  paid: "ຊຳລະແລ້ວ",
  voided: "ຍົກເລີກ",
} as const;

const DOCUMENT_STATUS_VARIANTS = {
  draft: "secondary",
  pending_approval: "secondary",
  approved: "default",
  issued: "outline",
  partial: "secondary",
  paid: "default",
  voided: "destructive",
} as const;

type DocumentTypeFilter = DocumentListItemDTO["documentType"] | "all";
type DocumentStatusFilter = DocumentListItemDTO["status"] | "all";
type EventFilter = string | "all";
type EventOption = {
  label: string;
  value: string;
};

function formatMoney(value: number, currency: DocumentCurrency = "LAK") {
  return formatDocumentMoney(value, currency);
}

function formatDate(value: Date | string | null) {
  if (!value) return "—";
  return new Intl.DateTimeFormat("lo-LA", {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(new Date(value));
}

function formatDateOnly(value: Date | string | null) {
  if (!value) return "—";
  return new Intl.DateTimeFormat("lo-LA", {
    dateStyle: "medium",
  }).format(new Date(value));
}

function dueRowClass(urgency: DocumentListItemDTO["dueUrgency"]) {
  if (urgency === "overdue") {
    return "bg-red-50/90 hover:bg-red-100/80 dark:bg-red-950/30 dark:hover:bg-red-950/45";
  }
  if (urgency === "warning") {
    return "bg-amber-50/90 hover:bg-amber-100/80 dark:bg-amber-950/25 dark:hover:bg-amber-950/40";
  }
  return "hover:bg-muted/20";
}

export function DocumentsPage() {
  const nav = useNavigate({ from: "/app/performance/documents" });
  const documentsQuery = useDocumentsQuery();
  const documents = documentsQuery.data ?? [];
  const [documentType, setDocumentType] = useState<DocumentTypeFilter>("all");
  const [status, setStatus] = useState<DocumentStatusFilter>("all");
  const [eventId, setEventId] = useState<EventFilter>("all");

  const eventOptions = useMemo(() => {
    const byId = new Map<string, string>();
    for (const document of documents) {
      byId.set(document.eventId, document.eventTitle ?? document.eventId);
    }
    return [...byId.entries()]
      .map(([value, label]) => ({ value, label }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [documents]);

  const queryEventOptions = ({
    search,
    pageParam,
  }: {
    search: string;
    pageParam: number;
  }) => {
    const pageSize = 20;
    const normalizedSearch = search.trim().toLowerCase();
    const matchingEvents = normalizedSearch
      ? eventOptions.filter((option) =>
          option.label.toLowerCase().includes(normalizedSearch),
        )
      : eventOptions;
    const start = (pageParam - 1) * pageSize;
    const items = matchingEvents.slice(start, start + pageSize);
    const nextPage =
      start + pageSize < matchingEvents.length ? pageParam + 1 : null;
    return Promise.resolve({ items, nextPage });
  };

  const preloadEventOption = (id: string) =>
    Promise.resolve(eventOptions.find((option) => option.value === id) ?? null);

  const filteredDocuments = useMemo(() => {
    const filtered = documents.filter((document) => {
      const matchesType =
        documentType === "all" || document.documentType === documentType;
      const matchesStatus = status === "all" || document.status === status;
      const matchesEvent = eventId === "all" || document.eventId === eventId;
      return matchesType && matchesStatus && matchesEvent;
    });

    const urgencyRank = (value: DocumentListItemDTO["dueUrgency"]) => {
      if (value === "overdue") return 0;
      if (value === "warning") return 1;
      if (value === "ok") return 2;
      return 3;
    };

    return [...filtered].sort((a, b) => {
      const rank = urgencyRank(a.dueUrgency) - urgencyRank(b.dueUrgency);
      if (rank !== 0) return rank;
      const aDue = a.dueAt
        ? new Date(a.dueAt).getTime()
        : Number.POSITIVE_INFINITY;
      const bDue = b.dueAt
        ? new Date(b.dueAt).getTime()
        : Number.POSITIVE_INFINITY;
      return aDue - bDue;
    });
  }, [documents, documentType, eventId, status]);

  const hasFilter =
    documentType !== "all" || status !== "all" || eventId !== "all";

  return (
    <>
      <Header>
        <Breadcrumb
          items={[
            { label: "ແຜງຄວບຄຸມ", to: "/app/dashboard" },
            { label: "ງານສະແດງ", to: "/app/performance/events" },
            { label: "ເອກະສານ" },
          ]}
        />
      </Header>

      <Main>
        <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
          <div>
            <h1 className="font-bold text-3xl tracking-tight">ເອກະສານລູກຄ້າ</h1>
            <p className="mt-1 text-muted-foreground text-sm">
              ໃບເກັບເງິນ: ກຳນົດຊຳລະພາຍໃນ 7–14 ວັນຫຼັງອອກເອກະສານ — ແຖວສີເຫຼືອງ = ໃກ້ກຳນົດ
              (≤7 ວັນ), ສີແດງ = ເກີນກຳນົດ · ໃບສະເໜີລາຄາແຈ້ງເຕືອນເມື່ອກຳນົດວັນໝົດອາຍຸແລ້ວເທົ່ານັ້ນ
            </p>
          </div>
        </div>

        <div className="mb-4 flex flex-col gap-3 px-2">
          <Tabs
            value={documentType}
            onValueChange={(value) =>
              setDocumentType(value as DocumentTypeFilter)
            }
          >
            <TabsList className="w-full justify-start overflow-x-auto sm:w-auto">
              <TabsTrigger value="all">ທັງໝົດ</TabsTrigger>
              <TabsTrigger value="quotation">
                {DOCUMENT_TYPE_LABELS.quotation}
              </TabsTrigger>
              <TabsTrigger value="invoice">
                {DOCUMENT_TYPE_LABELS.invoice}
              </TabsTrigger>
              <TabsTrigger value="receipt">
                {DOCUMENT_TYPE_LABELS.receipt}
              </TabsTrigger>
            </TabsList>
          </Tabs>

          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="flex flex-1 flex-wrap items-center gap-2">
              <InfiniteCombobox<EventOption>
                queryKey={["performance-document-events"]}
                queryFn={queryEventOptions}
                preloadQueryFn={preloadEventOption}
                getLabel={(item) => item.label}
                getValue={(item) => item.value}
                value={eventId === "all" ? "" : eventId}
                onValueChange={(value) => setEventId(value || "all")}
                placeholder="All events"
                className="h-8 w-full sm:w-64"
                clearable
              />

              <SimpleSelect
                value={status}
                onValueChange={(value) =>
                  setStatus(value as DocumentStatusFilter)
                }
                placeholder="Status"
                className="h-8 w-full sm:w-44"
                options={[
                  { value: "all", label: "ສະຖານະທັງໝົດ" },
                  { value: "draft", label: DOCUMENT_STATUS_LABELS.draft },
                  {
                    value: "pending_approval",
                    label: DOCUMENT_STATUS_LABELS.pending_approval,
                  },
                  { value: "approved", label: DOCUMENT_STATUS_LABELS.approved },
                  { value: "issued", label: DOCUMENT_STATUS_LABELS.issued },
                  { value: "partial", label: DOCUMENT_STATUS_LABELS.partial },
                  { value: "paid", label: DOCUMENT_STATUS_LABELS.paid },
                  { value: "voided", label: DOCUMENT_STATUS_LABELS.voided },
                ]}
              />
            </div>

            {hasFilter && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setDocumentType("all");
                  setStatus("all");
                  setEventId("all");
                }}
                className="max-sm:w-full"
              >
                <XIcon className="size-4" />
                Clear
              </Button>
            )}
          </div>
        </div>

        <div className="overflow-x-auto rounded-xl border bg-card">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b bg-muted/30">
                <th className="px-5 py-3 text-left font-medium text-muted-foreground text-xs uppercase tracking-wide">
                  ເອກະສານ
                </th>
                <th className="px-4 py-3 text-left font-medium text-muted-foreground text-xs uppercase tracking-wide">
                  ລູກຄ້າ
                </th>
                <th className="px-4 py-3 text-left font-medium text-muted-foreground text-xs uppercase tracking-wide">
                  ງານສະແດງ
                </th>
                <th className="px-4 py-3 text-left font-medium text-muted-foreground text-xs uppercase tracking-wide">
                  ສະຖານະ
                </th>
                <th className="px-4 py-3 text-left font-medium text-muted-foreground text-xs uppercase tracking-wide">
                  ກຳນົດ
                </th>
                <th className="px-4 py-3 text-right font-medium text-muted-foreground text-xs uppercase tracking-wide">
                  ຍອດລວມ
                </th>
                <th className="px-4 py-3 text-right font-medium text-muted-foreground text-xs uppercase tracking-wide">
                  ຈັດການ
                </th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {documentsQuery.isLoading ? (
                <tr>
                  <td
                    colSpan={7}
                    className="px-5 py-10 text-center text-muted-foreground"
                  >
                    ກຳລັງໂຫຼດເອກະສານ...
                  </td>
                </tr>
              ) : filteredDocuments.length === 0 ? (
                <tr>
                  <td
                    colSpan={7}
                    className="px-5 py-10 text-center text-muted-foreground"
                  >
                    ຍັງບໍ່ມີເອກະສານ
                  </td>
                </tr>
              ) : (
                filteredDocuments.map((document) => (
                  <tr
                    key={document.id}
                    className={dueRowClass(document.dueUrgency)}
                  >
                    <td className="px-5 py-3">
                      <div className="font-semibold">
                        {DOCUMENT_TYPE_LABELS[document.documentType]}
                      </div>
                      <div className="text-muted-foreground text-xs">
                        {document.documentNo}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm">
                      {document.customerName || "—"}
                    </td>
                    <td className="px-4 py-3">
                      <div className="font-medium">
                        {document.eventTitle ?? document.eventId}
                      </div>
                      <div className="mt-1 flex items-center gap-1 text-muted-foreground text-xs">
                        <CalendarIcon className="size-3.5" />
                        {formatDate(document.eventDate)}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <Badge
                        variant={
                          DOCUMENT_STATUS_VARIANTS[document.status] ?? "outline"
                        }
                      >
                        {DOCUMENT_STATUS_LABELS[document.status] ??
                          document.status}
                      </Badge>
                    </td>
                    <td className="px-4 py-3">
                      {document.dueUrgency === "overdue" ? (
                        <div className="space-y-1">
                          <Badge className="border-transparent bg-red-600 text-white hover:bg-red-600">
                            ເກີນກຳນົດ
                          </Badge>
                          <div className="text-red-700 text-xs dark:text-red-300">
                            {formatDateOnly(document.dueAt ?? null)}
                          </div>
                        </div>
                      ) : document.dueUrgency === "warning" ? (
                        <div className="space-y-1">
                          <Badge className="border-transparent bg-amber-500 text-white hover:bg-amber-500">
                            ໃກ້ເກີນກຳນົດ
                          </Badge>
                          <div className="text-amber-800 text-xs dark:text-amber-200">
                            {formatDateOnly(document.dueAt ?? null)}
                          </div>
                        </div>
                      ) : document.dueAt ? (
                        <span className="text-muted-foreground text-xs">
                          {formatDateOnly(document.dueAt)}
                        </span>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-right font-semibold">
                      {formatMoney(
                        document.grandTotal,
                        document.currency ?? "LAK",
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="mb-1 flex flex-wrap justify-end gap-1.5">
                        <Badge
                          variant={document.pdfFileId ? "default" : "secondary"}
                        >
                          {document.pdfFileId ? "PDF" : "Print"}
                        </Badge>
                      </div>
                      <div className="flex flex-wrap justify-end gap-1">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() =>
                            nav({
                              to: "/app/performance/events/$id",
                              params: { id: document.eventId },
                            })
                          }
                        >
                          <ExternalLinkIcon className="size-4" />
                          ເປີດງານ
                        </Button>
                        {document.status !== "voided" && (
                          <PerformanceBrandPrintMenu
                            variant="ghost"
                            size="sm"
                            preview={document.status === "draft"}
                            signatureToggle={{
                              defaultChecked:
                                document.status !== "draft" &&
                                document.status !== "voided",
                            }}
                            buildPrintUrl={(lang, options) =>
                              performanceApi.documents.printUrl(
                                document.id,
                                lang,
                                options,
                              )
                            }
                          />
                        )}
                        {document.pdfFileId && (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() =>
                              window.open(
                                performanceApi.documents.pdfUrl(document.id),
                                "_blank",
                              )
                            }
                          >
                            <DownloadIcon className="size-4" />
                            PDF
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </Main>
    </>
  );
}
